<template>
  <div>
    <b-sidebar
      id="sidebar-add-new-event"
      sidebar-class=""
      :visible="isEventHandlerSidebarActive"
      bg-variant="white"
      width="600px"
      shadow
      backdrop
      no-header
      right
      @change="(val) => $emit('update:is-event-handler-sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Modern Header -->
        <div class="sidebar-header-modern">
          <div class="sidebar-header-content">
            <div class="sidebar-title-wrapper">
              <div class="sidebar-title-icon-box">
                <feather-icon
                  :icon="eventLocal.id ? 'EditIcon' : 'PlusIcon'"
                  size="20"
                />
              </div>
              <h5 class="sidebar-title">
                {{ eventLocal.id ? "Edit Event Details" : "Create New Event" }}
              </h5>
            </div>
            <div class="header-actions d-flex align-items-center">
              <b-button
                v-if="eventLocal.id"
                variant="outline-danger"
                size="sm"
                class="sidebar-delete-btn mr-1"
                v-b-tooltip.hover.bottom="'Delete Event'"
                @click="
                  $emit('remove-event');
                  hide();
                "
              >
                <feather-icon icon="Trash2Icon" size="18" />
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="sidebar-close-btn"
                @click="hide"
              >
                <feather-icon icon="XIcon" size="20" />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="sidebar-body-modern">
          <validation-observer
            ref="refFormObserver"
            class="sidebar-form-modern"
          >
            <!-- Form -->
            <b-form class="p-2" @submit.prevent="handleFormSubmit">
              <!-- Title -->
              <validation-provider
                #default="validationContext"
                name="Title"
                rules="required"
              >
                <b-form-group
                  label="Event Title"
                  label-for="event-title"
                  class="modern-form-group"
                >
                  <b-form-input
                    id="event-title"
                    v-model="eventLocal.title"
                    autofocus
                    class="modern-input"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="e.g. Annual Sports Day"
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Event Type Selection -->
              <validation-provider
                #default="validationContext"
                name="Type"
                rules="required"
              >
                <b-form-group
                  label="Classification"
                  label-for="event-type"
                  class="modern-form-group"
                  :state="getValidationState(validationContext)"
                >
                  <div class="modern-toggle-group">
                    <input
                      type="hidden"
                      v-model="eventLocal.extendedProps.eventType"
                    />
                    <div
                      class="toggle-item"
                      :class="{
                        active:
                          eventLocal.extendedProps &&
                          eventLocal.extendedProps.eventType === 'event',
                      }"
                      @click="setEventType('event')"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="14"
                        class="mr-50"
                      />
                      Standard Event
                    </div>
                    <div
                      class="toggle-item holiday-toggle"
                      :class="{
                        active:
                          eventLocal.extendedProps &&
                          eventLocal.extendedProps.eventType === 'holiday',
                      }"
                      @click="setEventType('holiday')"
                    >
                      <feather-icon icon="SunIcon" size="14" class="mr-50" />
                      Public Holiday
                    </div>
                  </div>

                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Holiday Options -->
              <div
                v-if="
                  eventLocal &&
                  eventLocal.extendedProps &&
                  eventLocal.extendedProps.eventType === 'holiday' &&
                  isHeadOffice
                "
                class="holiday-options-container mb-2"
                key="holiday-options"
              >
                <b-card class="holiday-options-card shadow-sm border-dashed">
                  <div
                    class="holiday-checkbox-wrapper d-flex align-items-center"
                  >
                    <input
                      class="form-check-input custom-checkbox"
                      type="checkbox"
                      id="uploadToAllTeachers"
                      :checked="!!eventLocal.extendedProps.uploadToAllTeachers"
                      @change="handleCheckboxChange"
                    />
                    <label
                      class="form-check-label holiday-checkbox-label d-flex align-items-center"
                      for="uploadToAllTeachers"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="18"
                        class="text-primary mr-75 ml-1"
                      />
                      <span class="checkbox-text font-weight-semibold"
                        >Adjust planners dates accordingly</span
                      >
                    </label>
                    <div class="ml-auto tooltip-container">
                      <span
                        class="view-guide-text"
                        v-b-tooltip.hover.top
                        title="By enabling this feature, all your planner dates will be automatically updated and adjusted according to this holiday schedule. This ensures your teaching plans remain synchronized with holiday breaks."
                      >
                        <feather-icon
                          icon="HelpCircleIcon"
                          size="18"
                          class="mr-1"
                        />
                        View Guide
                      </span>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Calendar -->
              <validation-provider
                #default="validationContext"
                name="Event For"
                rules="required"
              >
                <b-form-group
                  label="Event For"
                  label-for="calendar"
                  class="modern-form-group"
                  :state="getValidationState(validationContext)"
                >
                  <v-select
                    v-model="eventLocal.extendedProps.calendar"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="calendarOptions"
                    label="label"
                    :reduce="(calendar) => calendar.label"
                    input-id="calendar"
                    multiple
                    :closeOnSelect="false"
                    class="modern-select"
                    placeholder="Select one or more options"
                  >
                    <template #option="{ color, label }">
                      <div
                        class="rounded-circle d-inline-block mr-50"
                        :class="`bg-${color}`"
                        style="height: 10px; width: 10px"
                      />
                      <span> {{ label }}</span>
                    </template>

                    <template #selected-option="{ color, label }">
                      <div
                        class="rounded-circle d-inline-block mr-50"
                        :class="`bg-${color}`"
                        style="height: 10px; width: 10px"
                      />
                      <span> {{ label }}</span>
                    </template>
                  </v-select>

                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Class & Sec -->
              <template
                v-if="
                  Array.isArray(eventLocal.extendedProps.calendar)
                    ? eventLocal.extendedProps.calendar.includes('Students')
                    : eventLocal.extendedProps.calendar == 'Students'
                "
              >
                <b-form-group
                  :label="getLabel('Class')"
                  label-for="class"
                  class="modern-form-group"
                >
                  <v-select
                    ref="class"
                    multiple
                    :closeOnSelect="false"
                    v-model="eventLocal.extendedProps.classID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="classesOptions"
                    :clearable="false"
                    label="name"
                    :reduce="(val) => val.id"
                    @input="FillSections()"
                    class="modern-select"
                    :placeholder="'Select ' + getLabel('class')"
                  />
                </b-form-group>

                <b-form-group
                  :label="getLabel('Section')"
                  label-for="section"
                  class="modern-form-group"
                >
                  <v-select
                    multiple
                    :closeOnSelect="false"
                    v-model="eventLocal.extendedProps.secID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="admittedSections"
                    :reduce="(val) => val.id"
                    :clearable="false"
                    ref="section"
                    label="section"
                    class="modern-select"
                    :placeholder="'Select ' + getLabel('section')"
                    @input="checkSections()"
                  />
                </b-form-group>
              </template>

              <!-- Department Selection for Staff -->
              <b-form-group
                label="Department"
                label-for="department"
                class="modern-form-group"
                v-if="
                  Array.isArray(eventLocal.extendedProps.calendar)
                    ? eventLocal.extendedProps.calendar.includes('Staff')
                    : eventLocal.extendedProps.calendar == 'Staff'
                "
              >
                <v-select
                  ref="department"
                  multiple
                  :closeOnSelect="false"
                  v-model="eventLocal.extendedProps.departmentID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="departmentOptions"
                  :clearable="false"
                  label="name"
                  :reduce="(val) => val.id"
                  class="modern-select"
                  placeholder="Select department"
                  @input="checkDepartments()"
                />
              </b-form-group>

              <!-- Start & End Date -->
              <b-row>
                <b-col md="6">
                  <validation-provider
                    #default="validationContext"
                    name="Start Date"
                    rules="required"
                  >
                    <b-form-group
                      label="Start Date"
                      label-for="start-date"
                      class="modern-form-group"
                      :state="getValidationState(validationContext)"
                    >
                      <flat-pickr
                        v-model="eventLocal.start"
                        class="modern-input"
                        :config="config"
                      />
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="6">
                  <validation-provider
                    #default="validationContext"
                    name="End Date"
                    :rules="{
                      required: true,
                      dateAfterStart: eventLocal.start,
                    }"
                  >
                    <b-form-group
                      label="End Date"
                      label-for="end-date"
                      class="modern-form-group"
                      :state="getValidationState(validationContext)"
                    >
                      <flat-pickr
                        v-model="eventLocal.end"
                        class="modern-input"
                        :config="configEnd"
                      />
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-row>

              <!-- Event Timing -->
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Start Time"
                    label-for="start-time"
                    class="modern-form-group"
                  >
                    <flat-pickr
                      v-model="eventLocal.extendedProps.startTime"
                      class="modern-input"
                      :config="timeConfig"
                      placeholder="Select start time"
                      :key="`start-time-${eventLocal.id || 'new'}`"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    name="End Time"
                    :rules="{
                      timeAfterStart: {
                        startTime: eventLocal.extendedProps.startTime,
                        startDate: eventLocal.start,
                        endDate: eventLocal.end,
                      },
                    }"
                  >
                    <b-form-group
                      label="End Time"
                      label-for="end-time"
                      class="modern-form-group"
                      :state="errors.length > 0 ? false : null"
                    >
                      <flat-pickr
                        v-model="eventLocal.extendedProps.endTime"
                        class="modern-input"
                        :config="timeConfig"
                        placeholder="Select end time"
                        :state="errors.length > 0 ? false : null"
                        :key="`end-time-${eventLocal.id || 'new'}`"
                      />
                      <b-form-invalid-feedback :state="false">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-row>

              <!-- Location Link -->
              <b-form-group
                label="Location Link"
                label-for="location-link"
                class="modern-form-group"
              >
                <b-form-input
                  id="location-link"
                  v-model="eventLocal.extendedProps.eventLink"
                  type="url"
                  trim
                  class="modern-input"
                  placeholder="https://www.example.com"
                />
              </b-form-group>

              <!-- Location -->
              <b-form-group
                label="Location"
                label-for="event-location"
                class="modern-form-group"
              >
                <b-form-input
                  id="event-location"
                  v-model="eventLocal.extendedProps.location"
                  trim
                  class="modern-input"
                  placeholder="Event Location"
                />
              </b-form-group>

              <!-- Contact Phone -->
              <b-form-group
                label="Contact Phone"
                label-for="contact-phone"
                class="modern-form-group"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Contact Phone"
                  rules="pakistan-phone"
                >
                  <b-form-input
                    id="contact-phone"
                    v-model="eventLocal.extendedProps.contactPhone"
                    trim
                    class="modern-input"
                    placeholder="Enter contact phone number"
                    :state="errors.length > 0 ? false : null"
                  />
                  <b-form-invalid-feedback :state="false">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>

              <!-- Textarea -->
              <b-form-group
                label="Description"
                label-for="event-description"
                class="modern-form-group"
              >
                <b-form-textarea
                  id="event-description"
                  v-model="eventLocal.extendedProps.description"
                  class="modern-input"
                  rows="6"
                />
              </b-form-group>

              <!-- Attachments -->
              <b-form-group label="Attachments" class="modern-form-group">
                <b-button
                  @click="$refs.mediaDoc.click()"
                  variant="primary"
                  :disabled="mydocloading"
                  block
                  class="modern-btn-primary"
                >
                  <b-spinner
                    v-if="mydocloading"
                    type="grow"
                    small
                    label="Loading..."
                  />
                  <span v-else>Upload Attachments</span>
                </b-button>
                <input
                  type="file"
                  id="mediaDoc"
                  hidden
                  ref="mediaDoc"
                  multiple
                  accept="image/*,video/*,.pdf,application/pdf"
                  @change="addMedia()"
                />
              </b-form-group>

              <!-- Attachment Display -->
              <b-row v-if="attachmentCount > 0" class="mt-2 mb-2">
                <b-col cols="12" class="d-flex flex-wrap">
                  <div
                    class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
                    style="width: 80px; height: 80px; position: relative"
                    v-for="(file, index) in attachments"
                    :key="`attachment-${file.url}-${index}`"
                    @click="openFile(file.url)"
                  >
                    <img
                      v-if="isImageAttachment(file)"
                      :src="getAttachmentPreview(file)"
                      alt=""
                      class="w-100 h-100 rounded event-attachment-preview"
                      referrerpolicy="no-referrer"
                      @error="onAttachmentPreviewError($event, file)"
                    />
                    <video
                      v-else-if="isVideoAttachment(file)"
                      :src="file.url"
                      class="w-100 h-100 rounded"
                      preload="metadata"
                      style="object-fit: contain"
                    />
                    <feather-icon
                      v-else
                      :icon="getFileIcon(file.type)"
                      size="30"
                      class="text-primary"
                    />
                    <b-avatar
                      size="20"
                      variant="primary"
                      style="position: absolute; top: -5px; right: -5px"
                      button
                      @click.stop="removeMedia(index)"
                    >
                      <feather-icon icon="XIcon" />
                    </b-avatar>
                  </div>
                </b-col>
              </b-row>

              <!-- Submit Button (Modern) -->
              <div class="sidebar-footer-modern mt-3">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="modern-btn-submit"
                  type="submit"
                  block
                  :disabled="isSubmitting"
                >
                  <b-spinner small v-if="isSubmitting" class="mr-50" />
                  <feather-icon
                    icon="SaveIcon"
                    size="16"
                    class="mr-1"
                    v-if="!isSubmitting"
                  />
                  <span>{{
                    eventLocal.id ? "Update Event" : "Save Event"
                  }}</span>
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </div>
      </template>
    </b-sidebar>

    <!-- Confirmation Modal -->
    <b-modal
      id="holiday-confirmation-modal"
      v-model="showConfirmationModal"
      title=""
      modal-class="custom-holiday-modal"
      header-class="d-none"
      body-class="p-0"
      footer-class="d-none"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header
      hide-footer
      size="sm"
    >
      <div class="modal-content-wrapper">
        <!-- Icon -->
        <div class="modal-icon">
          <feather-icon icon="CalendarIcon" size="32" class="text-primary" />
        </div>

        <!-- Content -->
        <div class="modal-text-content">
          <h5 class="modal-title mb-2">Adjust Planner Dates?</h5>
          <p class="modal-subtitle mb-3">
            These holiday will automatically adjust your planner schedules
          </p>

          <!-- Compact Info -->
          <div class="compact-info mb-3">
            <feather-icon icon="InfoIcon" size="16" class="text-info mr-2" />
            <span class="info-text"
              >All lesson plans will be automatically adjusted</span
            >
          </div>

          <p class="confirm-text mb-4">Continue with automatic adjustment?</p>
        </div>

        <!-- Buttons -->
        <div class="modal-actions">
          <b-button
            variant="outline-secondary"
            @click="cancelPlannerSubmission"
            class="btn-cancel"
          >
            No
          </b-button>
          <b-button
            variant="primary"
            @click="confirmPlannerSubmission"
            class="btn-confirm"
          >
            <feather-icon icon="CheckIcon" size="14" class="mr-1" />
            Yes
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BAvatar,
  BFormTextarea,
  BButton,
  BFormInvalidFeedback,
  BImg,
  BSpinner,
  BRow,
  BCol,
  BCard,
  BModal,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  required,
  email,
  url,
  pakistanPhone,
  dateAfterStart,
  timeAfterStart,
} from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { ref, toRefs } from "@vue/composition-api";
import useCalendarEventHandler from "./useCalendarEventHandler";

import { mapActions } from "vuex";
import {
  buildUploadedAttachment,
  getUploadedMediaType,
  uploadToMySkool,
  normalizeDocAttachment,
  isImageUrl,
  getMediaPreviewUrl,
  handleDrivePreviewError,
  getDocUrl,
} from "@/utils/gdriveUpload";

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BAvatar,
    vSelect,
    flatPickr,
    ValidationProvider,
    BFormInvalidFeedback,
    ValidationObserver,
    BImg,
    BSpinner,
    BRow,
    BCol,
    BCard,
    BModal,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  model: {
    prop: "isEventHandlerSidebarActive",
    event: "update:is-event-handler-sidebar-active",
  },
  props: {
    isEventHandlerSidebarActive: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    clearEventData: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      required,
      email,
      url,
      pakistanPhone,
      dateAfterStart,
      timeAfterStart,
      classes: [],
      classesOptions: [],
      admittedSections: [],
      departments: [],
      departmentOptions: [],
      attachments: [],
      mydocloading: false,
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d\\T11:00:00",
      },
      configEnd: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d\\T15:00:00",
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "h:i K",
        defaultHour: 9,
        defaultMinute: 0,
      },
      tooltipText: `When enabled, this holiday will be automatically added to all teachers' calendars in the system.`,
      showConfirmationModal: false,
      pendingSubmission: null,
      reportDomain: this.$store.state.report.replace(/_/g, ""),
    };
  },
  computed: {
    attachmentCount() {
      return this.attachments.length;
    },
    isHeadOffice() {
      const storeState =
        this.$store && this.$store.state ? this.$store.state : {};
      const branches = storeState.branches || [];
      const userData = storeState.userData || {};
      const campusId = userData.cId;
      const currentBranch =
        branches.find(
          (b) => b && (b.id === campusId || b.campusID === campusId)
        ) || {};
      const campusName = (
        currentBranch.branch ||
        currentBranch.name ||
        currentBranch.campus ||
        ""
      )
        .toString()
        .toLowerCase()
        .replace(/\s|-/g, "");
      return campusName === "headoffice" || campusName === "hq";
    },
  },
  setup(props, { emit }) {
    const clearFormData = ref(null);
    const attachmentsRef = ref([]);
    const refFormObserver = ref(null);
    const isSubmitting = ref(false);

    const {
      eventLocal,
      resetEventLocal,
      calendarOptions,
      onSubmit,
      guestsOptions,
    } = useCalendarEventHandler(
      toRefs(props),
      clearFormData,
      emit,
      attachmentsRef,
      refFormObserver
    );

    const { getValidationState, resetForm, clearForm } = formValidation(
      resetEventLocal,
      props.clearEventData
    );

    clearFormData.value = clearForm;

    return {
      // Add New Event
      eventLocal,
      calendarOptions,
      guestsOptions,
      attachmentsRef,
      isSubmitting,
      onSubmit, // Add this so component methods can access it

      // Form Validation
      resetForm,
      refFormObserver,
      getValidationState,
    };
  },
  watch: {
    // Watch for event changes to populate attachments
    event: {
      handler(newEvent) {
        console.log("Event watcher triggered:", newEvent);

        if (
          newEvent &&
          newEvent.extendedProps &&
          newEvent.extendedProps.attachments
        ) {
          console.log(
            "Loading attachments for event:",
            newEvent.extendedProps.attachments
          );
          this.attachments = newEvent.extendedProps.attachments.map((att) => {
            const doc = normalizeDocAttachment(att.url || att);
            return {
              ...att,
              url: doc.url,
              type: att.type || doc.type,
              previewUrl: att.previewUrl || doc.previewUrl,
            };
          });
        } else {
          this.attachments = [];
        }

        // Ensure extendedProps exists
        if (newEvent && !newEvent.extendedProps) {
          this.$set(newEvent, "extendedProps", {});
        }

        // Normalize and set defaults for edit/open
        if (newEvent && newEvent.extendedProps) {
          // Backward compatibility: map 'type' to 'eventType'
          if (
            !newEvent.extendedProps.eventType &&
            newEvent.extendedProps.type
          ) {
            this.$set(
              newEvent.extendedProps,
              "eventType",
              newEvent.extendedProps.type
            );
          }
          // Default to 'event' if still empty
          if (!newEvent.extendedProps.eventType) {
            this.$set(newEvent.extendedProps, "eventType", "event");
          }
          // Map adjustPlanner to uploadToAllTeachers if provided
          if (
            newEvent.extendedProps.uploadToAllTeachers === undefined &&
            newEvent.extendedProps.adjustPlanner !== undefined
          ) {
            this.$set(
              newEvent.extendedProps,
              "uploadToAllTeachers",
              Boolean(newEvent.extendedProps.adjustPlanner)
            );
          }
          // Ensure boolean default
          if (newEvent.extendedProps.uploadToAllTeachers === undefined) {
            this.$set(newEvent.extendedProps, "uploadToAllTeachers", false);
          }
        }

        // Force reactivity update
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
      deep: true,
      immediate: true,
    },

    // Sync attachments with reactive ref for composition API
    attachments: {
      handler(newVal) {
        console.log("Attachments watcher triggered:", newVal);
        console.log("Attachments length:", newVal.length);
        this.attachmentsRef = newVal;
      },
      deep: true,
      immediate: true,
    },

    // Set default department when Staff is selected
    "eventLocal.extendedProps.calendar": {
      handler(newVal) {
        // console.log("Calendar watcher triggered:", newVal);
        const isArray = Array.isArray(newVal);
        const includesStaff = isArray
          ? newVal.includes("Staff")
          : newVal === "Staff";
        const includesStudents = isArray
          ? newVal.includes("Students")
          : newVal === "Students";

        if (includesStaff) {
          // Set default to "All Departments" when Staff is selected
          this.$nextTick(() => {
            if (
              !this.eventLocal.extendedProps.departmentID ||
              this.eventLocal.extendedProps.departmentID.length === 0
            ) {
              this.$set(this.eventLocal.extendedProps, "departmentID", [0]);
            }
          });
        } else {
          // Clear department selection when Staff is not selected
          this.$nextTick(() => {
            this.$set(this.eventLocal.extendedProps, "departmentID", []);
          });
        }

        if (includesStudents) {
          // update section dropdown when Students is selected
          this.FillSections();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.admittedSections = [
      { section: "All " + this.getLabel("Sections"), id: 0 },
    ];
    this.LoadClasses();
    this.LoadDepartments();
  },
  methods: {
    ...mapActions({
      get: "get",
      post: "post",
      put: "put",
      delete: "delete",
    }),

    getLabel(key) {
      const isHappiness = this.reportDomain == "myskoolhappinessfoundation";
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    // Add hide method
    hide() {
      this.$emit("update:is-event-handler-sidebar-active", false);
    },

    async LoadClasses() {
      var obj2 = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      this.classes = await this.get(obj2);
      this.classesOptions = this.classes.map((el) => el.cls);
      this.classesOptions.unshift({
        id: 0,
        name: "All " + this.getLabel("Classes"),
      });
      // console.log(this.classes, this.classesOptions);
    },

    async LoadDepartments() {
      var obj = {
        url:
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.departments = await this.get(obj);
      this.departmentOptions = this.departments.map((el) => ({
        id: el.id,
        name: el.name,
      }));
      this.departmentOptions.unshift({ id: 0, name: "All Departments" });
    },

    FillSections() {
      if (
        this.eventLocal.extendedProps.classID[0] == 0 &&
        this.eventLocal.extendedProps.classID.length !== 1
      ) {
        this.eventLocal.extendedProps.classID =
          this.eventLocal.extendedProps.classID.filter((e) => e !== 0);
        // console.log(this.eventLocal.extendedProps.classID);
        this.classes.forEach((elem) => {
          if (this.eventLocal.extendedProps.classID.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
                ...elem.sections,
              ];
              // elem.sections.forEach((el) => this.admittedSections.push(el));
            } else {
              // console.log("null");
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (
        this.eventLocal.extendedProps.classID[
          this.eventLocal.extendedProps.classID.length - 1
        ] !== 0 &&
        this.eventLocal.extendedProps.classID.length == 1
      ) {
        this.classes.forEach((elem) => {
          if (this.eventLocal.extendedProps.classID.includes(elem.cls.id)) {
            if (elem.sections.length != 0) {
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
                ...elem.sections,
              ];
              // elem.sections.forEach((el) => this.admittedSections.push(el));
            } else {
              // console.log("null");
              this.admittedSections = [
                { section: "All " + this.getLabel("Sections"), id: 0 },
              ];
            }
          }
        });
      } else if (
        this.eventLocal.extendedProps.classID[
          this.eventLocal.extendedProps.classID.length - 1
        ] == 0
      ) {
        this.eventLocal.extendedProps.classID = [0];
        // console.log(this.eventLocal.extendedProps.classID);
        this.admittedSections = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.eventLocal.extendedProps.secID = [0];
      } else {
        this.admittedSections = [
          { section: "All " + this.getLabel("Sections"), id: 0 },
        ];
        this.eventLocal.extendedProps.secID = [0];
      }
    },
    checkSections() {
      // console.log(this.eventLocal.extendedProps.secID);
      if (
        this.eventLocal.extendedProps.secID[0] == 0 &&
        this.eventLocal.extendedProps.secID.length !== 1
      ) {
        this.eventLocal.extendedProps.secID =
          this.eventLocal.extendedProps.secID.filter((e) => e !== 0);
        // console.log(this.eventLocal.extendedProps.secID);
      } else if (
        this.eventLocal.extendedProps.secID[
          this.eventLocal.extendedProps.secID.length - 1
        ] == 0
      ) {
        this.eventLocal.extendedProps.secID = [0];
        // console.log(this.eventLocal.extendedProps.secID);
      }
    },

    // Attachment methods
    addMedia() {
      let media = this.$refs.mediaDoc.files;
      if (media.length > 0) {
        this.mydocloading = true;
        uploadToMySkool(media)
          .then((resp) => {
            resp.forEach((el) => {
              this.attachments.push(
                buildUploadedAttachment(el, {
                  id: 0,
                  name: getUploadedMediaType(el),
                })
              );
            });
            this.attachmentsRef = [...this.attachments];
            console.log(
              "Attachments added, total count:",
              this.attachments.length
            );
          })
          .catch((e) => {
            console.log(e);
          })
          .finally(() => {
            this.mydocloading = false;
          });
      }
    },

    removeMedia(index) {
      console.log("Removing attachment at index:", index);
      console.log("Attachments before removal:", this.attachments.length);
      this.attachments.splice(index, 1);
      console.log("Attachments after removal:", this.attachments.length);
      // Force reactivity update
      this.$forceUpdate();
      // Ensure the reactive ref is updated
      this.attachmentsRef = [...this.attachments];
      console.log(
        "Attachment removed, remaining attachments:",
        this.attachments
      );
    },

    debugAttachments() {
      console.log("=== ATTACHMENT DEBUG ===");
      console.log("Attachments array:", this.attachments);
      console.log("Attachments length:", this.attachments.length);
      console.log("AttachmentsRef:", this.attachmentsRef);
      console.log("Computed attachmentCount:", this.attachmentCount);
    },

    getType(url) {
      if (/\.(jpg|jpeg|png|webp|avif|gif|svg)$/i.test(url)) return "Image";
      else if (/\.(mp4|amv|webm)$/i.test(url)) return "Video";
      else if (/\.(pdf|doc|ppt|docx|xls|css|txt|xlsx)$/i.test(url))
        return "pdf";
      else return "Link";
    },

    normalizeAttachment(file) {
      const doc = normalizeDocAttachment(file?.url || file);
      return {
        ...file,
        url: doc.url,
        type: file?.type || doc.type,
        previewUrl: file?.previewUrl || doc.previewUrl,
      };
    },

    isImageAttachment(file) {
      const doc = this.normalizeAttachment(file);
      if (isImageUrl(doc.url, doc.type)) return true;
      if (this.isPdfAttachment(file) || this.isVideoAttachment(file)) {
        return false;
      }
      return /drive\.google\.com|googleusercontent\.com|cloudious\.net|gdrive\./i.test(
        doc.url
      );
    },

    isVideoAttachment(file) {
      const doc = this.normalizeAttachment(file);
      const type = String(doc.type || "").toLowerCase();
      if (type === "video") return true;
      return /\.(mp4|webm|mov|amv)(\?|#|$)/i.test(doc.url);
    },

    isPdfAttachment(file) {
      const doc = this.normalizeAttachment(file);
      const type = String(doc.type || "").toLowerCase();
      if (type === "pdf") return true;
      return /\.(pdf|doc|ppt|docx|xls|xlsx)(\?|#|$)/i.test(doc.url);
    },

    getAttachmentPreview(file) {
      const doc = this.normalizeAttachment(file);
      return doc.previewUrl || getMediaPreviewUrl(doc.url, "Image");
    },

    onAttachmentPreviewError(event, file) {
      const doc = this.normalizeAttachment(file);
      handleDrivePreviewError(event, doc.url, "image");
    },

    openFile(url) {
      window.open(getDocUrl(url), "_blank");
    },

    getFileIcon(type) {
      if (type) {
        if (type.toLowerCase() === "image") return "ImageIcon";
        else if (type.toLowerCase() === "video") return "VideoIcon";
        else if (type.toLowerCase() === "pdf") return "FileTextIcon";
        else return "LinkIcon";
      }
      return "LinkIcon";
    },

    checkDepartments() {
      // Handle department selection logic similar to sections
      if (
        this.eventLocal.extendedProps.departmentID &&
        this.eventLocal.extendedProps.departmentID[0] == 0 &&
        this.eventLocal.extendedProps.departmentID.length !== 1
      ) {
        // If "All Departments" is selected along with other departments, remove "All Departments"
        this.eventLocal.extendedProps.departmentID =
          this.eventLocal.extendedProps.departmentID.filter((e) => e !== 0);
      } else if (
        this.eventLocal.extendedProps.departmentID &&
        this.eventLocal.extendedProps.departmentID[
          this.eventLocal.extendedProps.departmentID.length - 1
        ] == 0
      ) {
        // If "All Departments" is the last selected, keep only "All Departments"
        this.eventLocal.extendedProps.departmentID = [0];
      }
    },

    setEventType(type) {
      console.log("Setting event type to:", type);

      // Ensure extendedProps exists
      if (!this.eventLocal.extendedProps) {
        this.$set(this.eventLocal, "extendedProps", {});
      }

      // Set the event type
      this.$set(this.eventLocal.extendedProps, "eventType", type);

      // Initialize uploadToAllTeachers if setting to holiday
      if (
        type === "holiday" &&
        this.eventLocal.extendedProps.uploadToAllTeachers === undefined
      ) {
        this.$set(this.eventLocal.extendedProps, "uploadToAllTeachers", false);
      }

      // Force reactivity update
      this.$nextTick(() => {
        this.$forceUpdate();
        console.log(
          "Event type set to:",
          this.eventLocal.extendedProps.eventType
        );
        console.log("Holiday options should show:", type === "holiday");
      });
    },

    handleCheckboxChange(event) {
      // Allow the checkbox to be checked/unchecked immediately without showing modal
      this.$set(
        this.eventLocal.extendedProps,
        "uploadToAllTeachers",
        event.target.checked
      );

      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Modal methods
    cancelPlannerSubmission() {
      this.showConfirmationModal = false;
      this.isSubmitting = false;
    },

    async confirmPlannerSubmission() {
      this.showConfirmationModal = false;
      try {
        this.isSubmitting = true;
        await this.onSubmit();
      } catch (error) {
        console.error("Error during planner submission:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    // Main submission handler
    async handleFormSubmit() {
      try {
        console.log("Submit clicked - starting validation");
        const isValid = await this.$refs.refFormObserver.validate();
        console.log("Validation result:", isValid);

        if (isValid) {
          console.log("Validation passed - checking for planner adjustment");

          // Check if this is a holiday with planner adjustment enabled
          const isHoliday =
            this.eventLocal.extendedProps &&
            this.eventLocal.extendedProps.eventType === "holiday";
          const hasPlannerAdjustment =
            this.eventLocal.extendedProps &&
            this.eventLocal.extendedProps.uploadToAllTeachers;

          if (isHoliday && hasPlannerAdjustment) {
            console.log(
              "Holiday with planner adjustment - showing confirmation modal"
            );
            // Show the confirmation modal
            this.showConfirmationModal = true;
          } else {
            console.log("Regular submission - calling onSubmit directly");
            this.isSubmitting = true;
            await this.onSubmit();
            this.isSubmitting = false;
          }
        } else {
          console.log("Validation failed");
        }
      } catch (error) {
        console.error("Error during form submission:", error);
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.border-dashed {
  border: 1px solid var(--primary);
  border-style: dashed;
}

.cursor-pointer {
  cursor: pointer;
}

.event-attachment-preview {
  object-fit: cover;
  display: block;
}

// Modern toggle buttons for Event Type
.modern-toggle {
  .toggle-btn {
    border-radius: 8px !important;
    font-weight: 600;
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    input[type="radio"] {
      display: none;
    }

    &:first-child {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-right: 1px solid rgba(0, 0, 0, 0.125);
    }

    &:last-child {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      border-left: 1px solid rgba(0, 0, 0, 0.125);
    }

    &.btn-outline-primary {
      &:hover {
        background-color: rgba(115, 103, 240, 0.1);
        border-color: #7367f0;
        color: #7367f0;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(115, 103, 240, 0.2);
      }

      &.active {
        background-color: #7367f0;
        border-color: #7367f0;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(115, 103, 240, 0.4);
        transform: translateY(-2px);
      }
    }

    &.btn-outline-success {
      &:hover {
        background-color: rgba(40, 199, 111, 0.1);
        border-color: #28c76f;
        color: #28c76f;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(40, 199, 111, 0.2);
      }

      &.active {
        background-color: #28c76f;
        border-color: #28c76f;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(40, 199, 111, 0.4);
        transform: translateY(-2px);
      }
    }
  }
}

// Holiday options container
.holiday-options-container {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.holiday-options-card {
  border: 2px solid #28c76f;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(40, 199, 111, 0.05) 0%,
    rgba(40, 199, 111, 0.02) 100%
  );
  transition: all 0.3s ease;

  &:hover {
    border-color: #28c76f;
    box-shadow: 0 8px 25px rgba(40, 199, 111, 0.15);
    transform: translateY(-2px);
  }

  .card-body {
    padding: 1rem;
  }
}

.holiday-checkbox-wrapper {
  margin-bottom: 0;
  padding: 1rem;
  width: 100%;

  .custom-checkbox {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid #28c76f;
    border-radius: 6px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    cursor: pointer;
    margin-right: 0.75rem;
    flex-shrink: 0;

    &:checked {
      background-color: #28c76f;
      border-color: #28c76f;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
      background-size: 100% 100%;
      background-position: center;
      background-repeat: no-repeat;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(40, 199, 111, 0.25);
      outline: none;
    }
  }

  .holiday-checkbox-label {
    font-weight: 600;
    color: #5e5873;
    cursor: pointer;
    margin-bottom: 0;
    transition: all 0.3s ease;
    flex-grow: 1;
    margin-left: 0;

    .checkbox-text {
      font-size: 0.95rem;
      color: #6e6b7b;
      transition: color 0.3s ease;
      margin-left: 0.5rem;
    }

    &:hover {
      .checkbox-text {
        color: #28c76f;
      }
    }
  }

  .tooltip-container {
    flex-shrink: 0;

    .view-guide-text {
      display: inline-flex;
      align-items: center;
      font-size: 0.875rem;
      font-weight: 600;
      color: #6c757d;
      cursor: help;
      padding: 0.5rem 0.75rem;
      border-radius: 20px;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;
      text-decoration: none;
      white-space: nowrap;

      &:hover {
        color: #28c76f;
        background: linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%);
        border-color: #28c76f;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 199, 111, 0.2);

        .feather {
          color: #28c76f;
        }
      }

      .feather {
        color: #6c757d;
        transition: color 0.3s ease;
      }
    }

    .tooltip-icon {
      opacity: 0.7;
      transition: all 0.3s ease;
      cursor: help;
      padding: 0.25rem;

      &:hover {
        opacity: 1;
        color: #28c76f !important;
        transform: scale(1.1);
      }
    }
  }
}

// Custom tooltip styles - improved
.tooltip {
  z-index: 9999 !important;
  pointer-events: none;
}

::v-deep .tooltip {
  z-index: 9999 !important;
  pointer-events: none;

  .tooltip-inner {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    color: #ffffff !important;
    padding: 0.75rem 1rem !important;
    border-radius: 8px !important;
    font-size: 0.85rem !important;
    font-weight: 500 !important;
    text-align: center !important;
    box-shadow: 0 4px 12px rgba(44, 62, 80, 0.3) !important;
    max-width: 280px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.4 !important;
    border: 2px solid #28c76f !important;
  }

  &.bs-tooltip-top .arrow::before {
    border-top-color: #28c76f !important;
  }

  &.bs-tooltip-bottom .arrow::before {
    border-bottom-color: #28c76f !important;
  }

  &.bs-tooltip-left .arrow::before {
    border-left-color: #28c76f !important;
  }

  &.bs-tooltip-right .arrow::before {
    border-right-color: #28c76f !important;
  }

  .arrow {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
}

// Confirmation Modal Styles
::v-deep .custom-holiday-modal {
  .modal-dialog {
    max-width: 360px;
    margin: 1.75rem auto;
  }

  .modal-content {
    border: none;
    border-radius: 20px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    background: #ffffff;
  }
}

.modal-content-wrapper {
  padding: 2rem 1.5rem;
  text-align: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

/* --- MODERN SIDEBAR THEME (RESOURCES STYLE) --- */

.sidebar-header-modern {
  background: #ffffff;
  border-bottom: 2px solid #e0d8ff;
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: 4px; // Space for gradient line

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .sidebar-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
  }

  .sidebar-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .sidebar-title-icon-box {
    width: 40px;
    height: 40px;
    background: rgba(124, 93, 250, 0.08);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7c5dfa;
  }

  .sidebar-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    margin: 0;
  }
}

.sidebar-body-modern {
  padding: 1.5rem;
  background: #ffffff;
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1.5rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
      display: block;
      text-transform: none;
      letter-spacing: normal;
    }
  }

  .modern-input {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    min-height: 44px;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;
    width: 100%;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &.is-invalid {
      border-color: #ef4444;
    }
  }

  .modern-select {
    ::v-deep .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.625rem 0.875rem;
      min-height: 44px;
      background: #fff;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    &.is-invalid {
      ::v-deep .vs__dropdown-toggle {
        border-color: #ef4444;
      }
    }
  }
}

// PREMIUM TOGGLE SYSTEM
.modern-toggle-group {
  display: flex;
  background: #f8fafc;
  padding: 6px;
  border-radius: 14px;
  gap: 6px;
  border: 1.5px solid #e0d8ff;

  .toggle-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(0, 0, 0, 0.02);
      color: #334155;
    }

    &.active {
      background: #fff;
      color: #7c5dfa;
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.1);

      &.holiday-toggle {
        color: #ff9f43;
      }
    }
  }
}

.holiday-options-container {
  animation: slideDown 0.4s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.holiday-options-card {
  border: 1.5px dashed rgba(124, 93, 250, 0.3) !important;
  background: rgba(124, 93, 250, 0.02) !important;
  border-radius: 16px;

  .checkbox-text {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2d1b69;
  }

  .custom-checkbox {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid #7c5dfa;
    border-radius: 6px;
  }
}

.sidebar-footer-modern {
  padding: 1.5rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border-top: 2px solid #e0d8ff;
  position: sticky;
  bottom: 0;
  z-index: 100;
  margin: 1.5rem -1.5rem -1.5rem -1.5rem;
}

.modern-btn-submit,
.modern-btn-primary {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border: none !important;
  border-radius: 12px !important;
  height: 52px;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(124, 93, 250, 0.35);
  }
}

.sidebar-close-btn,
.sidebar-delete-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #e0d8ff !important;
  background: #fff !important;
  color: #64748b !important;
  transition: all 0.3s ease;

  &:hover {
    background: #f3f0ff !important;
    border-color: #b8a9ff !important;
    color: #7c5dfa !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.15);
  }
}

.sidebar-delete-btn {
  border-color: #fee2e2 !important;
  color: #ef4444 !important;

  &:hover {
    background: #fef2f2 !important;
    border-color: #fca5a5 !important;
    color: #dc2626 !important;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  }
}

.modal-content-wrapper {
  padding: 3rem 2.5rem;
  text-align: center;

  .modal-icon {
    width: 72px;
    height: 72px;
    background: #f3f0ff;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.75rem;
    color: #7c5dfa;
    box-shadow: 0 12px 24px rgba(124, 93, 250, 0.12);
    animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 0.75rem;
    letter-spacing: -0.5px;
  }

  .modal-subtitle {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 2rem;
    padding: 0 1rem;
  }

  .compact-info {
    display: inline-flex;
    align-items: center;
    background: #f0f9ff;
    padding: 0.875rem 1.25rem;
    border-radius: 14px;
    color: #0284c7;
    font-weight: 600;
    font-size: 0.85rem;
    border: 1px solid #e0f2fe;
    margin-bottom: 2rem;

    .info-text {
      line-height: 1.4;
    }
  }

  .confirm-text {
    font-weight: 700;
    color: #334155;
    font-size: 1rem;
    margin-bottom: 1.75rem;
  }

  .modal-actions {
    display: flex;
    justify-content: center;
    gap: 16px; // Space between buttons

    .btn {
      flex: 1;
      height: 48px;
      border-radius: 12px;
      font-weight: 700;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.25s ease;

      &.btn-cancel {
        background: #fff;
        border: 1.5px solid #ebe9f1;
        color: #6e6b7b;

        &:hover {
          background: #f8f8f8;
          border-color: #d8d6de;
          color: #5e5873;
          transform: translateY(-2px);
        }
      }

      &.btn-confirm {
        background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
        border: none;
        color: #fff;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(124, 93, 250, 0.25);
        }

        &:active {
          transform: translateY(-1px);
        }
      }
    }
  }
}

@keyframes bounceIn {
  from {
    opacity: 0;
    transform: scale(0.3);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

// Modal Animation Enhancement
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

::v-deep .modal.show .modal-dialog {
  animation: modalSlideIn 0.36s cubic-bezier(0.34, 1.56, 0.64, 1);
}

::v-deep .modal-content {
  border-radius: 28px !important;
  border: none !important;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15) !important;
}
</style>
